import React, { useState, useRef } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from "recharts";
import "./CompoundInterestCalculator.css";
import SimpleHeader from "../NewApp/SimpleHeader";
import Button from "../reusable/Button/Button";
import Footer from "../Footer/Footer";

interface YearlyData {
  year: number;
  startBalance: number;
  interestEarned: number;
  contributions: number;
  endBalance: number;
}

const COMPOUND_OPTIONS = [
  { label: "Տարեկան", value: 1 },
  { label: "Տարին 2 անգամ", value: 2 },
  { label: "Քառամսյակային", value: 4 },
  { label: "Ամսական", value: 12 },
  { label: "Շաբաթական", value: 52 },
  { label: "Օրական", value: 365 },
];

type Currency = "USD" | "AMD";

const LABELS: Record<Currency, {
  title: string;
  principal: string;
  principalHint: string;
  rateHint: string;
  compound: string;
  compoundHint: string;
  contribution: string;
  contributionHint: string;
  contributionFreq: string;
  finalBalance: string;
  earned: string;
  chartTitle: string;
  tableTitle: string;
  balanceName: string;
  contributionsName: string;
  tableBalance: string;
  tableInterest: string;
  tableContributions: string;
}> = {
  USD: {
    title: "Բարդ տոկոսի հաշվիչ",
    principal: "Սկզբնական բալանս",
    principalHint: "Պատրաստի գումար, որով կարող եք սկսել",
    rateHint: "Սա Ձեր ներդրումներից սպասվող տարեկան տոկոսն է։ Պատմականորեն, S&P 500-ի տարեկան տոկոսը եղել է 10-12%-ի մեջ՝ 30 տարվա կտրվածքով։",
    compound: "Կապիտալացման հաճախականություն",
    compoundHint: "Ներդրումներում հիմնականում տարեկան ենք դիտարկում",
    contribution: "Պարբերական ներդրում",
    contributionHint: "Գումար, որ կարող եք ներդնել ինչ-որ հաճախականությամբ",
    contributionFreq: "Ներդրման հաճախականությունը",
    finalBalance: "Վերջնական բալանս",
    earned: "Գեներացված եկամուտը",
    chartTitle: "Տարիների ընթացքում աճը",
    tableTitle: "Տարեկան փոփոխությունները",
    balanceName: "Բալանս",
    contributionsName: "Ընդհանուր ներդրումներ",
    tableBalance: "Սկզբնական բալանս",
    tableInterest: "Ստացված տոկոս",
    tableContributions: "Ընդհանուր ներդրումներ",
  },
  AMD: {
    title: "Ավանդի հաշվիչ",
    principal: "Ավանդի սկզբնական գումար",
    principalHint: "Սկզբնական գումար",
    rateHint: "Հայաստանում բանկային ավանդների տոկոսադրույքը սովորաբար կազմում է 7–12% տարեկան։ Ստուգեք Ձեր բանկի պայմանները։",
    compound: "Կապիտալացման հաճախականություն",
    compoundHint: "Հայկական բանկերում ամսական կամ տարեկան կապիտալացումն ամենատարածվածն է",
    contribution: "Համալրում",
    contributionHint: "Ավանդին ավելացվող պարբերական գումար",
    contributionFreq: "Համալրման հաճախականություն",
    finalBalance: "Ավանդի վերջնական արժեք",
    earned: "Կուտակված տոկոս",
    chartTitle: "Ավանդի աճը տարիների ընթացքում",
    tableTitle: "Տարեկան կուտակումը",
    balanceName: "Ավանդ",
    contributionsName: "Ընդհանուր ավանդ",
    tableBalance: "Սկզբնական գումար",
    tableInterest: "Կուտակված տոկոս",
    tableContributions: "Ընդհանուր ավանդ",
  },
};

const CompoundInterestCalculator: React.FC = () => {
  const [currency, setCurrency] = useState<Currency>("USD");
  const [principal, setPrincipal] = useState("1000");
  const [annualRate, setAnnualRate] = useState("8");
  const [years, setYears] = useState("20");
  const [compoundPerYear, setCompoundPerYear] = useState(12);
  const [contribution, setContribution] = useState("0");
  const [contributionFreq, setContributionFreq] = useState(12);

  // Cache inputs per currency so switching feels natural
  const cache = useRef<Record<Currency, {
    principal: string; annualRate: string; years: string;
    compoundPerYear: number; contribution: string; contributionFreq: number;
  }>>({
    USD: { principal: "1000", annualRate: "8", years: "20", compoundPerYear: 12, contribution: "0", contributionFreq: 12 },
    AMD: { principal: "100000", annualRate: "8", years: "20", compoundPerYear: 12, contribution: "0", contributionFreq: 12 },
  });

  const switchCurrency = (next: Currency) => {
    // Save current inputs to cache
    cache.current[currency] = { principal, annualRate, years, compoundPerYear, contribution, contributionFreq };
    // Restore cached inputs for next currency
    const c = cache.current[next];
    setPrincipal(c.principal);
    setAnnualRate(c.annualRate);
    setYears(c.years);
    setCompoundPerYear(c.compoundPerYear);
    setContribution(c.contribution);
    setContributionFreq(c.contributionFreq);
    setCurrency(next);
    setResult(null);
    setYearlyData([]);
  };
  const [yearlyData, setYearlyData] = useState<YearlyData[]>([]);
  const [result, setResult] = useState<number | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const sym = currency === "AMD" ? "֏" : "$";
  const L = LABELS[currency];

  const fmt = (v: number) =>
    v.toLocaleString(undefined, { maximumFractionDigits: 2 });

  const calculate = () => {
    const P = parseFloat(principal);
    const r = parseFloat(annualRate) / 100;
    const n = Number(compoundPerYear);
    const t = Number(years);
    const c = parseFloat(contribution);
    const cf = Number(contributionFreq);

    if ([P, r, n, t, c, cf].some((v) => isNaN(v) || v < 0) || n < 1 || cf < 1) {
      setMessage("Խնդրում ենք մուտքագրել վավեր դրական արժեքներ։");
      setYearlyData([]);
      setResult(null);
      return;
    }

    let balance = P;
    let data: YearlyData[] = [];
    let cumulativeContributions = P;

    for (let year = 1; year <= t; year++) {
      let startBalance = balance;
      let yearlyInterest = 0;

      for (let period = 0; period < n; period++) {
        const periodContrib = c * (cf / n);
        balance += periodContrib;
        cumulativeContributions += periodContrib;

        const periodInterest = balance * (r / n);
        balance += periodInterest;
        yearlyInterest += periodInterest;
      }

      data.push({
        year,
        startBalance,
        interestEarned: yearlyInterest,
        contributions: cumulativeContributions,
        endBalance: balance,
      });
    }

    setYearlyData(data);
    setResult(balance);
    setMessage(null);
  };

  const getTotalContributions = () => {
    if (yearlyData.length === 0) return 0;
    return yearlyData[yearlyData.length - 1].contributions;
  };

  return (
    <>
      <SimpleHeader onHome={() => window.location.href = '/'} onResources={() => window.location.href = '/resources'} />
      <div className="ci-root">
        <div className="ci-panel left">
          <div className="ci-header-row">
            <h1>{L.title}</h1>
            <div className="ci-currency-toggle">
              <button
                className={`ci-cur-btn ${currency === "USD" ? "active" : ""}`}
                onClick={() => switchCurrency("USD")}
              >$ USD</button>
              <button
                className={`ci-cur-btn ${currency === "AMD" ? "active" : ""}`}
                onClick={() => switchCurrency("AMD")}
              >֏ AMD</button>
            </div>
          </div>

          <div className="ci-group">
            <label>{L.principal}</label>
            <input
              type="number"
              value={principal}
              onChange={(e) => setPrincipal(e.target.value)}
              min="0"
            />
            <span className="unit">{sym}</span>
            <div className="hint">{L.principalHint}</div>
          </div>
          <div className="ci-group">
            <label>Տարեկան տոկոսադրույք</label>
            <input
              type="number"
              value={annualRate}
              onChange={(e) => setAnnualRate(e.target.value)}
              min="0"
              step="0.01"
            />
            <span className="unit">%</span>
            <div className="hint">{L.rateHint}</div>
          </div>
          <div className="ci-group">
            <label>Տարիներ</label>
            <input
              type="number"
              value={years}
              onChange={(e) => setYears(e.target.value)}
              min="1"
            />
          </div>
          <div className="ci-group">
            <label>{L.compound}</label>
            <select
              value={compoundPerYear}
              onChange={(e) => setCompoundPerYear(Number(e.target.value))}
            >
              {COMPOUND_OPTIONS.map((opt) => (
                <option value={opt.value} key={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="hint">{L.compoundHint}</div>
          </div>
          <div className="ci-group">
            <label>{L.contribution}</label>
            <input
              type="number"
              value={contribution}
              onChange={(e) => setContribution(e.target.value)}
              min="0"
            />
            <span className="unit">{sym}</span>
            <div className="hint">{L.contributionHint}</div>
          </div>
          <div className="ci-group">
            <label>{L.contributionFreq}</label>
            <select
              value={contributionFreq}
              onChange={(e) => setContributionFreq(Number(e.target.value))}
            >
              <option value={1}>Տարեկան</option>
              <option value={12}>Ամսական</option>
              <option value={52}>Շաբաթական</option>
            </select>
          </div>
          <Button onClick={calculate}>Հաշվել</Button>
          {message && <div className="ci-error">{message}</div>}
        </div>

        {(result !== null || yearlyData.length > 0) && (
          <div className="ci-panel right">
            {result !== null && (
              <div className="ci-result-card">
                <div className="ci-result-label">{L.finalBalance}</div>
                <div className="ci-result-amount">
                  {sym}{fmt(result)}
                </div>
                <div className="ci-result-interest">
                  {L.earned}: {sym}{fmt(result - getTotalContributions())}
                </div>
                {currency === "AMD" && (
                  <div className="ci-result-note">* Հարկը և միջնորդավճարները հաշվի չեն առնված</div>
                )}
              </div>
            )}

            {yearlyData.length > 0 && (
              <>
                <div className="ci-graph-section">
                  <h2>{L.chartTitle}</h2>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={yearlyData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis />
                      <Tooltip
                        formatter={(value: any) =>
                          `${sym}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                        }
                        labelFormatter={(label) => `Տարի: ${label}`}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="endBalance"
                        stroke="#ff7043"
                        fill="#ffab91"
                        name={L.balanceName}
                      />
                      <Area
                        type="monotone"
                        dataKey="contributions"
                        stroke="#1976d2"
                        fill="#bbdefb"
                        name={L.contributionsName}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="ci-table-section">
                  <h2>{L.tableTitle}</h2>
                  <div className="ci-table-scroll">
                    <table className="ci-result-table">
                      <thead>
                        <tr>
                          <th>Տարի</th>
                          <th>{L.tableBalance}</th>
                          <th>{L.tableInterest}</th>
                          <th>{L.tableContributions}</th>
                          <th>{L.finalBalance}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {yearlyData.map((row) => (
                          <tr key={row.year}>
                            <td>{row.year}</td>
                            <td>{sym}{fmt(row.startBalance)}</td>
                            <td>{sym}{fmt(row.interestEarned)}</td>
                            <td>{sym}{fmt(row.contributions)}</td>
                            <td>{sym}{fmt(row.endBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default CompoundInterestCalculator;
